import type { AdjustRequest, ApiClient, Appointment, ListedServicePrice, ServiceChoice } from "@shyne/shared/api";
import type { uuid } from "@/interfaces";
import { typedPrice } from "./appointment-price";

type AdjustApi = Pick<ApiClient["appointments"], "adjust">;

export type AppointmentAdjustment = Omit<AdjustRequest, "actingOrganization">;

export function createAdjustment(api: AdjustApi) {
  return function adjust(id: uuid, adjustment: AppointmentAdjustment, actingOrganization: uuid | null): Promise<Appointment> {
    return api.adjust(id, { ...adjustment, ...(actingOrganization ? { actingOrganization } : {}) });
  };
}

export type Payee = { name: string; amount: number | null; paid: boolean };

type PaidAppointment = {
  assigned_organization?: { id: uuid | null; display_name: string | null } | null;
  assigned_user: { id: uuid | null; name: string | null };
  subs_payment?: number | null;
  subs_payment_status?: string | null;
  individual_payment?: number | null;
  individual_payment_status?: string | null;
};

export function payeeOf({ assigned_organization, assigned_user, subs_payment, subs_payment_status, individual_payment, individual_payment_status }: PaidAppointment): Payee | null {
  if (assigned_organization?.id) {
    return subs_payment === undefined ? null : { name: assigned_organization.display_name ?? "the subcontractor", amount: subs_payment, paid: subs_payment_status === "paid" };
  }
  if (assigned_user.id) {
    return individual_payment === undefined ? null : { name: assigned_user.name ?? "the worker", amount: individual_payment, paid: individual_payment_status === "paid" };
  }
  return null;
}

export type Adjustable = { service: uuid; price: number | null; pay: number | null; priceMissing: boolean };

export type AdjustmentDraft = { service: uuid; price: string; pay: string | null };

export const leavesPriceUnset = (start: Adjustable, draft: AdjustmentDraft): boolean => draft.service === start.service && start.price === null && draft.price.trim() === "";

export function adjustmentOf(start: Adjustable, draft: AdjustmentDraft): AppointmentAdjustment | null {
  const price = leavesPriceUnset(start, draft) ? undefined : typedPrice(draft.price);
  const pay = draft.pay === null || draft.pay.trim() === "" ? undefined : typedPrice(draft.pay);
  if (draft.service === "" || price === null || pay === null) return null;
  const sameService = draft.service === start.service;
  const adjustment: AppointmentAdjustment = {
    ...(sameService ? {} : { service: draft.service }),
    ...(price === undefined || (sameService && price === start.price && !start.priceMissing) ? {} : { price }),
    ...(pay === undefined || pay === start.pay ? {} : { pay }),
  };
  return Object.keys(adjustment).length === 0 ? null : adjustment;
}

export type ListedMoney = { price: number | null; pay: number | null };

export const listedMoneyOf = ({ price, pay, payFrom }: Pick<ListedServicePrice, "price" | "pay" | "payFrom">): ListedMoney => ({ price, pay: payFrom === null ? null : pay });

const moneyText = (amount: number | null) => (amount === null ? "" : String(amount));

export function draftFor(start: Adjustable, service: uuid, listed: ListedMoney | null, payShown: boolean): AdjustmentDraft {
  const money = service === start.service || listed === null ? start : { price: listed.price, pay: listed.pay ?? start.pay };
  return { service, price: moneyText(money.price), pay: payShown ? moneyText(money.pay) : null };
}

type CurrentService = ServiceChoice & { parent_name: string };

export function serviceChoices(category: string, live: readonly ServiceChoice[], current: CurrentService): ServiceChoice[] {
  const keepsCurrent = category === current.parent_name && !live.some(({ id }) => id === current.id);
  return keepsCurrent ? [...live, { id: current.id, name: current.name }] : [...live];
}
